/**
 * Role View Context
 * Allows admins to preview the app as different roles
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { USER_ROLES, type UserRole } from '../shared/constants';

interface RoleViewContextValue {
  viewAsRole: UserRole | null;
  actualRole: UserRole | null;
  effectiveRole: UserRole | null;
  isImpersonating: boolean;
  setViewAsRole: (role: UserRole | null) => void;
  resetView: () => void;
  canImpersonate: boolean;
}

const RoleViewContext = createContext<RoleViewContextValue | undefined>(undefined);

export function useRoleView() {
  const context = useContext(RoleViewContext);
  if (!context) {
    throw new Error('useRoleView must be used within RoleViewProvider');
  }
  return context;
}

interface RoleViewProviderProps {
  children: React.ReactNode;
  userRole: UserRole | null;
}

const STORAGE_KEY = 'qbank_view_as_role';

export function RoleViewProvider({ children, userRole }: RoleViewProviderProps) {
  const [viewAsRole, setViewAsRoleState] = useState<UserRole | null>(() => {
    // Restore from localStorage for all users (not just admin)
    // Only allow User role view if they're not already a User
    if (userRole && userRole !== USER_ROLES.USER) {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Only allow User role switching
      if (stored === USER_ROLES.USER) {
        return stored as UserRole;
      }
    }
    return null;
  });

  // Can switch view if user is not already a User
  const canImpersonate = userRole !== USER_ROLES.USER;

  // Effective role is the impersonated role if active, otherwise actual role
  const effectiveRole = viewAsRole || userRole;
  const isImpersonating = !!viewAsRole && canImpersonate;

  // Clear impersonation if user role changes or becomes User
  useEffect(() => {
    if (!canImpersonate && viewAsRole) {
      setViewAsRoleState(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [canImpersonate, viewAsRole]);

  const setViewAsRole = (role: UserRole | null) => {
    if (!canImpersonate) {
      console.warn('User role cannot switch views');
      return;
    }

    // Only allow switching to User role
    if (role !== null && role !== USER_ROLES.USER) {
      console.warn('Can only switch to User view');
      return;
    }

    setViewAsRoleState(role);

    if (role) {
      localStorage.setItem(STORAGE_KEY, role);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const resetView = () => {
    setViewAsRole(null);
  };

  const value: RoleViewContextValue = {
    viewAsRole,
    actualRole: userRole,
    effectiveRole,
    isImpersonating,
    setViewAsRole,
    resetView,
    canImpersonate,
  };

  return (
    <RoleViewContext.Provider value={value}>
      {children}
    </RoleViewContext.Provider>
  );
}
